import { z } from 'zod';
import { AuditedFields, QueryBoolean, Uuid, Version } from './common.js';
/**
 * Catalogue de vente (ADR-016, D-H08) : catégories et produits d'un
 * établissement, groupes d'options (variantes, accompagnements, suppléments).
 *
 * Le catalogue est porté par l'**établissement** ; une catégorie peut être
 * réservée à un point de vente (`outletId`). Les prix sont dans la devise du
 * tenant ; les taxes viennent des règles fiscales (`taxRuleCodes`), jamais du code.
 */
export const CategoryColor = z.enum(['GREEN', 'GOLD', 'SLATE', 'OLIVE', 'TERRACOTTA']);
/** Icône Lucide (nom en kebab-case, ex. `utensils`, `wine`, `coffee`). */
export const CategoryIcon = z
    .string()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Nom d’icône Lucide en kebab-case')
    .max(40);
export const CatalogCategory = z
    .object({
    id: Uuid,
    propertyId: Uuid,
    /** Point de vente auquel la catégorie est réservée ; `null` = tous les points de vente du site. */
    outletId: Uuid.nullable(),
    code: z.string(),
    name: z.string(),
    icon: z.string(),
    color: CategoryColor,
    sortOrder: z.number().int(),
    isActive: z.boolean(),
    /** Destination d'impression par défaut des produits de la catégorie (ADR-017). */
    printDestinationId: Uuid.nullable(),
    productsCount: z.number().int().nonnegative(),
    version: Version,
})
    .merge(AuditedFields);
export const CreateCategoryRequest = z.object({
    propertyId: Uuid,
    outletId: Uuid.nullable().optional(),
    code: z.string().trim().min(2).max(20),
    name: z.string().trim().min(2).max(80),
    icon: CategoryIcon.default('utensils'),
    color: CategoryColor.default('GREEN'),
    sortOrder: z.number().int().min(0).max(9999).default(0),
    printDestinationId: Uuid.nullable().optional(),
});
export const UpdateCategoryRequest = z.object({
    outletId: Uuid.nullable().optional(),
    name: z.string().trim().min(2).max(80).optional(),
    icon: CategoryIcon.optional(),
    color: CategoryColor.optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    isActive: z.boolean().optional(),
    printDestinationId: Uuid.nullable().optional(),
    version: Version,
});
export const ListCategoriesQuery = z.object({
    propertyId: Uuid.optional(),
    /** Catégories visibles depuis ce point de vente (réservées à lui ou communes). */
    outletId: Uuid.optional(),
    includeInactive: QueryBoolean.optional(),
});
// ---------------------------------------------------------------------------
// Options (variantes, accompagnements, suppléments)
// ---------------------------------------------------------------------------
export const OptionGroupKind = z.enum(['SINGLE', 'MULTI']);
export const ProductOption = z.object({
    id: Uuid,
    name: z.string(),
    /** Supplément (ou réduction, négatif) appliqué au prix unitaire. */
    priceDelta: z.number(),
    isActive: z.boolean(),
    sortOrder: z.number().int(),
});
export const ProductOptionGroup = z.object({
    id: Uuid,
    name: z.string(),
    kind: OptionGroupKind,
    /** Nombre minimal de choix (1 = obligatoire). */
    minChoices: z.number().int().nonnegative(),
    /** Nombre maximal de choix (`null` = illimité, `MULTI` seulement). */
    maxChoices: z.number().int().positive().nullable(),
    sortOrder: z.number().int(),
    options: z.array(ProductOption),
});
export const ProductOptionInput = z.object({
    name: z.string().trim().min(1).max(60),
    priceDelta: z.number().default(0),
    isActive: z.boolean().default(true),
    sortOrder: z.number().int().min(0).max(999).optional(),
});
export const OptionGroupInput = z
    .object({
    name: z.string().trim().min(1).max(60),
    kind: OptionGroupKind,
    minChoices: z.number().int().min(0).max(20).default(0),
    maxChoices: z.number().int().min(1).max(20).nullable().default(null),
    sortOrder: z.number().int().min(0).max(999).optional(),
    options: z.array(ProductOptionInput).min(1).max(40),
})
    .superRefine((group, ctx) => {
    if (group.kind === 'SINGLE' && group.maxChoices !== null && group.maxChoices !== 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['maxChoices'],
            message: 'Un groupe à choix unique accepte au plus une option',
        });
    }
    if (group.maxChoices !== null && group.minChoices > group.maxChoices) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['minChoices'],
            message: 'Le minimum ne peut pas dépasser le maximum',
        });
    }
});
// ---------------------------------------------------------------------------
// Produits
// ---------------------------------------------------------------------------
export const CatalogProduct = z
    .object({
    id: Uuid,
    propertyId: Uuid,
    categoryId: Uuid,
    code: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    price: z.number(),
    currencyCode: z.string().length(3),
    unit: z.string().nullable(),
    /** Codes de règles fiscales appliquées (ex. `TVA18`) ; vide = aucune taxe. */
    taxRuleCodes: z.array(z.string()),
    isActive: z.boolean(),
    /** Faux = rupture : le produit reste dans la carte mais ne se commande plus. */
    isAvailable: z.boolean(),
    /** Destination d'impression propre ; `null` = celle de la catégorie. */
    printDestinationId: Uuid.nullable(),
    sortOrder: z.number().int(),
    optionGroups: z.array(ProductOptionGroup),
    version: Version,
})
    .merge(AuditedFields);
export const CreateProductRequest = z.object({
    categoryId: Uuid,
    code: z.string().trim().min(1).max(30),
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).nullable().optional(),
    price: z.number().nonnegative(),
    unit: z.string().trim().max(20).nullable().optional(),
    taxRuleCodes: z.array(z.string().trim().min(1).max(20)).max(10).default([]),
    printDestinationId: Uuid.nullable().optional(),
    sortOrder: z.number().int().min(0).max(9999).default(0),
    optionGroups: z.array(OptionGroupInput).max(10).default([]),
});
export const UpdateProductRequest = z.object({
    categoryId: Uuid.optional(),
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().trim().max(500).nullable().optional(),
    price: z.number().nonnegative().optional(),
    unit: z.string().trim().max(20).nullable().optional(),
    taxRuleCodes: z.array(z.string().trim().min(1).max(20)).max(10).optional(),
    isActive: z.boolean().optional(),
    isAvailable: z.boolean().optional(),
    printDestinationId: Uuid.nullable().optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    /** Remplace l'ensemble des groupes d'options (les commandes passées gardent leurs options figées). */
    optionGroups: z.array(OptionGroupInput).max(10).optional(),
    version: Version,
});
export const ListProductsQuery = z.object({
    propertyId: Uuid.optional(),
    categoryId: Uuid.optional(),
    /** Produits vendables depuis ce point de vente (catégories communes ou réservées à lui). */
    outletId: Uuid.optional(),
    q: z.string().trim().max(80).optional(),
    includeInactive: QueryBoolean.optional(),
});
//# sourceMappingURL=catalog.js.map